import {cs} from "../../react/chain-services";
import {Dropdown} from "../dropdown/dropdown";
import {css, cx} from "emotion";
import * as React from "react";
import "./select-2.scss";
import {ClassNameArg} from "create-emotion";

interface UniqueProps<Item> {
    className                ?: ClassNameArg,
    disableClearingSelection ?: boolean,
    disabled                 ?: boolean,
    getLabel                  : (item:Item) => string,
    isSelected                : (item:Item) => boolean,
    list                      : Item[],
    onSelect                  : (item:Item) => void,
    placeholder              ?: JSX.Element,
    style                    ?: {height?: number},
    xdQaName                 ?: string,
}

export const Select2 = <Item,>(
    {
        list, style=undefined, placeholder=undefined, disabled=false, 
        getLabel, onSelect, isSelected, disableClearingSelection=false, 
        className=undefined, xdQaName=undefined, ...props
    }: UniqueProps<Item> & Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, keyof UniqueProps<Item>>
) => cs(
    ["selectedIndex", (_, next) => next(list?.findIndex(isSelected) ?? -1)],
    ["small", (_, next) => next(style?.height < 36)],
    ({selectedIndex, small}) => Dropdown({
        forcedExpandBottom: true,
        forcedExpandLeft: true,
        renderToggle: ({domRef, showExpand}) => (
            <div
                {...props}
                className={cx(
                    "select-2 select-2-29i toggle",
                    className,
                    {active: selectedIndex > -1, small},
                    css`
                        --height: ${style?.height ?? 36}px;
                    `,
                )}
                ref={domRef}
                onClick={() => !disabled && showExpand()}
                onFocus={() => !disabled && showExpand()}
                xd-qa-type="dropdown"
                xd-qa-name={xdQaName}
            >
                {selectedIndex === -1 ? (
                    <div
                        className="text placeholder"
                        xd-qa-type="dropdown_placeholder"
                        xd-qa-name="Placeholder"
                    >
                        {placeholder}
                    </div>
                ) : (
                    <div
                        className="text"
                        xd-qa-type="dropdown_selected"
                        xd-qa-name={"Selected " + xdQaName}
                    >
                        {getLabel(list[selectedIndex])}
                    </div>
                )}
                {!small ? (
                    <div className="icon">
                        <i className="fa fa-angle-up"/>
                        <i className="fa fa-angle-down"/>
                    </div>
                ) : (
                    <div className="icon small">
                        <i className="fa fa-angle-down"/>
                    </div>
                )}
            </div>
        ),
        renderExpand: ({close, width, position, domRef}) => cs(
            ({}) => (
                <div
                    xd-qa-type="dropdown"
                    xd-qa-name={xdQaName}
                    className="select-2-29i expand"
                    style={{
                        ...position,
                        top: position.top-1,
                        width,
                        maxHeight: window.innerHeight - position.top + 1,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    onWheel={(e) => {
                        e.stopPropagation();
                    }}
                    ref={domRef}
                >
                    <div className="list">
                        {list?.map((item, i) => {
                            const selected = i === selectedIndex;
                            return (
                                <div
                                    key={i}
                                    className={cx("item", {selected})}
                                    onClick={() => {
                                        if (selected) {
                                            return;
                                        }
                                        onSelect(item);
                                        close();
                                    }}
                                >
                                    <div className="content">
                                        <div 
                                            className="text" 
                                            xd-qa-type="dropdown_item"
                                            xd-qa-name={getLabel(item)}
                                            title={getLabel(item)}
                                        >
                                            {getLabel(item)}
                                        </div>

                                        {selected && (
                                            <i className="fa fa-check"/>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {selectedIndex > -1 && !disableClearingSelection && (
                        <div className="footer">
                            <a
                                onClick={() => {
                                    close();
                                    onSelect(null);
                                }}
                                xd-qa-type="link"
                                xd-qa-name="Clear Selection"
                            >
                                Clear Selection
                            </a>
                        </div>
                    )}
                </div>
            )
        ),
    })
);
